import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'src/checkout/context';
import { DUTCHIE_PAY, FASTER } from 'src/payments/constants';
import { useIncentives } from 'src/payments/hooks/use-incentives/use-incentives';
import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';

import { useCartPayment } from 'src/checkout/components/sections/payment/shared/use-cart-payment';
import { TabValue, usePaymentSelectionContext } from 'src/checkout/components/sections/payment/shared/context';
import { isTippableMethod } from 'src/checkout/components/sections/payment/shared/utils';
import { OrderType } from 'src/gql/graphql';
import { TabOption } from './payment-tabs';
import { BoltIcon } from './icons/bolt-icon';

export type UseSelectionsResponse = {
  showPaymentBanner: boolean;
  showCashBackRedesign: boolean;
  onlinePaymentOptionIsDutchiePay: boolean;
  tabOptions: TabOption[];
  onTabChange: (newPaymentMethod: string) => void;
  tabValue: TabValue;
  showFeeBadgeInline: boolean;
  showTabs: boolean;
  showOnlinePaymentOptionOnly: boolean;
  showInstorePaymentOptionOnly: boolean;
  showPaymentSummary: boolean;
  plaidEmbeddedSearchVisible: boolean;
  unenrolledAlertMessage: string;
};

export const useSelections = (): UseSelectionsResponse => {
  const flags = useFlags();
  const {
    expanded,
    setExpanded,
    tabValue,
    setTabValue,
    creditCardError,
    setCreditCardError,
    manuallyExpanded,
  } = usePaymentSelectionContext();

  const disableTabFees: boolean = flags['fintech.platform.remove-tab-fees.rollout'];
  const tipEligibilityValidationEnabled: boolean = flags['fintech.platform.validate-tip-eligibility.rollout'] ?? false;
  // Gates the render-triggered serviceFeeShown telemetry so it can be rolled out gradually rather than
  // hitting 100% of checkout traffic at once (fintech.platform.service-fee-shown-telemetry.rollout).
  const serviceFeeShownTelemetryEnabled: boolean = flags['fintech.platform.service-fee-shown-telemetry.rollout'];
  // Hooks
  const {
    isEnrolledDutchiePay,
    isDutchiePayEnabledForDispo,
    paymentMethod,
    paymentMethodId,
    hasOtherOrderTypesWithPBBEnabled,
    onlinePaymentOption,
    onlinePaymentOptionValue,
    hasOnlineAndOfflinePaymentOptions,
    onlinePaymentOptionFeeLabel,
    maxInstoreFeeLabel,
    onlinePaymentOptionIsCreditCard,
    onlinePaymentOptionIsDutchiePay,
    defaultOfflinePaymentValue,
    offlinePaymentOptionValues,
    loggedInPBBSignupEnabled,
    needsRelinkBankAccount: relinkRequired,
  } = useCartPayment();

  const { Cart, User } = useStores();
  const { orderType, dispensary } = Cart;

  const { id } = User ?? {};
  const { showPaymentBanner, qualifiedPBBUser, payerId, showCashBackRedesign, incentiveValue } = useIncentives();
  const {
    trackPaymentMethodSelected,
    trackServiceFeeShown,
    trackCheckoutFlowAction,
    hasSessionToken,
  } = useDutchiePayAnalytics();
  const Checkout = useCheckout();
  const feeShownTrackedRef = useRef(false);
  const manuallyExpandedRef = useRef(manuallyExpanded);

  useEffect(() => {
    manuallyExpandedRef.current = manuallyExpanded;
  }, [manuallyExpanded]);

  const requirePaymentMethodSelection = dispensary?.storeSettings?.requirePaymentMethodSelection ?? false;

  const checkoutPaymentError = Checkout.getError({ card: 'payment' });

  const isDeliveryOrder = orderType === OrderType.delivery;

  const onlinePaymentError =
    tabValue === TabValue.online && (!!checkoutPaymentError?.error || relinkRequired || creditCardError !== '');

  const unenrolledAlertMessage = checkoutPaymentError.error ? checkoutPaymentError.message : '';

  const tabOptions = useMemo(
    () =>
      hasOnlineAndOfflinePaymentOptions
        ? [
            {
              value: TabValue.online,
              label: 'Pay now',
              sublabel: disableTabFees ? '' : '(faster)',
              badgeLabel: onlinePaymentOptionFeeLabel,
              badgeIcon: onlinePaymentOptionFeeLabel === FASTER ? <BoltIcon /> : undefined,
              hasError: onlinePaymentError,
              callout:
                showCashBackRedesign && onlinePaymentOptionIsDutchiePay
                  ? { label: `$${String(incentiveValue)} back` }
                  : undefined,
            },
            {
              value: TabValue.offline,
              label: `Pay ${isDeliveryOrder ? 'on delivery' : 'in-store'}`,
              badgeLabel: maxInstoreFeeLabel,
            },
          ]
        : [],
    [
      hasOnlineAndOfflinePaymentOptions,
      onlinePaymentOptionFeeLabel,
      maxInstoreFeeLabel,
      isDeliveryOrder,
      disableTabFees,
      onlinePaymentError,
      onlinePaymentOptionIsDutchiePay,
      showCashBackRedesign,
      incentiveValue,
    ]
  );

  // Show
  const isLoggedIn = !!id;

  const showTabs = expanded && hasOnlineAndOfflinePaymentOptions && tabOptions.length > 0;

  const showOnlinePaymentOptionOnly =
    expanded && !showTabs && (!!onlinePaymentOption || hasOtherOrderTypesWithPBBEnabled);

  const showInstorePaymentOptionOnly = expanded && !showTabs && offlinePaymentOptionValues.includes(paymentMethod);
  const showPaymentSummary = !expanded && !!onlinePaymentOption;
  const plaidEmbeddedSearchVisible = tabValue === TabValue.online && loggedInPBBSignupEnabled;

  // Emit "service fee shown" once each time the fee tabs become visible (Q1: does seeing the fee affect conversion).
  // Wait for the session token before firing (and before latching the ref): payment selection starts expanded, so
  // this effect often runs on first mount while useDutchiePayAnalytics is still resolving the token. Emitting early
  // would post the event with an empty dutchie_pay_session_token and permanently latch it, breaking the join key.
  useEffect(() => {
    if (!showTabs) {
      feeShownTrackedRef.current = false;
      return;
    }
    if (feeShownTrackedRef.current || !hasSessionToken || !serviceFeeShownTelemetryEnabled) {
      return;
    }
    feeShownTrackedRef.current = true;
    trackServiceFeeShown({
      feesDisabled: disableTabFees,
      onlinePaymentMethod: onlinePaymentOptionValue,
      onlineFeeLabel: onlinePaymentOptionFeeLabel,
      instoreFeeLabel: maxInstoreFeeLabel,
    });
  }, [
    showTabs,
    hasSessionToken,
    serviceFeeShownTelemetryEnabled,
    disableTabFees,
    onlinePaymentOptionValue,
    onlinePaymentOptionFeeLabel,
    maxInstoreFeeLabel,
    trackServiceFeeShown,
  ]);

  const setPaymentMethod = useCallback(
    (method: string, methodId: number | string | null, expand: boolean): void => {
      Cart.setPaymentMethod(method);
      if (tipEligibilityValidationEnabled && !isTippableMethod(method, orderType, dispensary)) {
        Cart.removeTip();
      }
      Cart.setPaymentMethodId(methodId);
      if (expand || !manuallyExpandedRef.current) {
        setExpanded(expand);
      }
    },
    [Cart, dispensary, orderType, setExpanded, tipEligibilityValidationEnabled]
  );

  // PBB may only be auto-selected as this order's payment method when it is enabled for the
  // selected order type. `onlinePaymentOption` comes from options that are enabled for the
  // current order type, so a Dutchie Pay value there means the dispensary allows PBB here.
  const pbbEnabledForSelectedOrderType = isDutchiePayEnabledForDispo && onlinePaymentOptionValue === DUTCHIE_PAY;

  // Incentive metadata can hydrate after the shopper expands payment details. Keep those updates
  // separate so they do not re-run auto-selection and collapse a manual choice.
  useEffect(() => {
    if (paymentMethod !== DUTCHIE_PAY) {
      return;
    }
    Cart.setIsFirstTimePBBUser(qualifiedPBBUser);
    Cart.setPayerId(payerId);
  }, [Cart, payerId, paymentMethod, qualifiedPBBUser]);

  // Effect: Sets the payment method based on Dutchie Pay eligibility and enrollment status.
  // - If requirePaymentMethodSelection is enabled and both online/offline options exist, don't pre-select.
  // - If Dutchie Pay is enabled for the selected order type:
  //   - Check if the user is enrolled in Dutchie Pay.
  //   - Set the payment method to Dutchie Pay, using the payment method ID if enrolled, otherwise null.
  // - Otherwise, set the payment method to the online payment option if it's a credit card, or to the default offline payment value.
  // - When PBB is enabled only for other order types, the online tab still opens for the PBB signup
  //   prompt, but the order keeps a payment method that is valid for the selected order type.
  useEffect(() => {
    // When requirePaymentMethodSelection is enabled and there are both payment options, don't auto-select
    if (requirePaymentMethodSelection && hasOnlineAndOfflinePaymentOptions) {
      setPaymentMethod('', null, true);
      setTabValue(TabValue.none);
      Checkout.removeError({ card: 'payment' });
      return;
    }

    if (pbbEnabledForSelectedOrderType) {
      setPaymentMethod(
        DUTCHIE_PAY,
        isEnrolledDutchiePay ? paymentMethodId : null,
        !isEnrolledDutchiePay || relinkRequired
      );
      setTabValue(TabValue.online);
    } else if (onlinePaymentOptionIsCreditCard) {
      setPaymentMethod(onlinePaymentOptionValue, null, true);
      setTabValue(TabValue.online);
    } else {
      // set the payment method to the default offline payment value
      setPaymentMethod(defaultOfflinePaymentValue, null, true);
      setTabValue(hasOtherOrderTypesWithPBBEnabled ? TabValue.online : TabValue.offline);
    }
    Checkout.removeError({ card: 'payment' });
  }, [
    onlinePaymentOptionIsCreditCard,
    onlinePaymentOptionValue,
    isEnrolledDutchiePay,
    paymentMethodId,
    setPaymentMethod,
    Cart,
    hasOnlineAndOfflinePaymentOptions,
    requirePaymentMethodSelection,
    orderType,
    isLoggedIn,
    defaultOfflinePaymentValue,
    setTabValue,
    relinkRequired,
    Checkout,
    pbbEnabledForSelectedOrderType,
    hasOtherOrderTypesWithPBBEnabled,
  ]);

  // When the tab changes, set the payment method to the selected tabs payment method
  const onTabChange = (selectedTab: TabValue): void => {
    const newPaymentMethod = selectedTab === TabValue.online ? onlinePaymentOptionValue : defaultOfflinePaymentValue;
    Cart.setPaymentMethod(newPaymentMethod);
    // PBB conversion funnel: record the payment method the consumer selected and whether PBB was offered,
    // so non-PBB selections (drop-off) can be joined to the funnel via dutchie_pay_session_token.
    trackPaymentMethodSelected({
      paymentMethod: newPaymentMethod,
      pbbOffered: isDutchiePayEnabledForDispo && onlinePaymentOptionValue === DUTCHIE_PAY,
      isPbb: newPaymentMethod === DUTCHIE_PAY,
    });
    // Q7 funnel context: which payment mode (pay-now vs in-store) the shopper chose.
    trackCheckoutFlowAction({
      action: 'payment_mode_selected',
      value: selectedTab === TabValue.online ? 'pay_now' : 'pay_in_store',
    });
    // Clear tip when switching to a payment method that doesn't support tipping
    if (!isTippableMethod(newPaymentMethod, orderType, dispensary)) {
      Cart.removeTip();
    }
    setTabValue(selectedTab);
    Checkout.removeError({ card: 'payment' });
    setCreditCardError('');
    if (newPaymentMethod === DUTCHIE_PAY) {
      Cart.setPaymentMethodId(paymentMethodId);
      Cart.setIsFirstTimePBBUser(qualifiedPBBUser);
      Cart.setPayerId(payerId);
    } else {
      Cart.setPaymentMethodId(null);
      Cart.setIsFirstTimePBBUser(false);
      Cart.setPayerId(null);
    }
  };

  return {
    showPaymentBanner,
    showCashBackRedesign,
    onlinePaymentOptionIsDutchiePay,
    tabOptions,
    onTabChange,
    tabValue,
    showFeeBadgeInline: disableTabFees,
    showTabs,
    showOnlinePaymentOptionOnly,
    showInstorePaymentOptionOnly,
    showPaymentSummary,
    plaidEmbeddedSearchVisible,
    unenrolledAlertMessage,
  };
};
